import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, doc, deleteDoc, runTransaction, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useRoles } from '../context/RolesContext';
import { useAppConfig } from '../context/AppConfigContext';
import { isAdminEmail } from '../utils/admin';

export default function Forums() {
  const { user } = useAuth();
  const { adminEmails } = useAppConfig();
  const { isMod } = useRoles();
  const navigate = useNavigate();
  const isAdmin = isAdminEmail(user?.email, adminEmails);
  const [threads, setThreads] = useState([]);
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'threads'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Pinned first locally; Firestore orderBy on boolean isn't ideal
        items.sort((a, b) => (b.pinned === true) - (a.pinned === true));
        setThreads(items);
      },
      (err) => {
        console.error('Failed to load threads:', err);
        setError(err.message || 'Failed to load threads');
      }
    );
    return () => unsub();
  }, []);

  const createThread = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!title.trim()) return;
    setCreating(true);
    setError('');
    try {
      const docRef = await addDoc(collection(db, 'threads'), {
        title: title.trim(),
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorPhotoURL: user.photoURL || null,
        createdAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        replies: 0,
        locked: false,
        pinned: false,
        score: 0,
      });
      setTitle('');
      navigate(`/forums/${docRef.id}`);
    } catch (err) {
      console.error('Create thread failed:', err);
      setError(`[${err.code || 'error'}] ${err.message || 'Failed to create thread'}`);
    } finally {
      setCreating(false);
    }
  };

  const togglePin = async (threadId, current) => {
    try {
      await updateDoc(doc(db, 'threads', threadId), { pinned: !current });
    } catch (err) {
      console.error('Pin toggle failed', err);
      setError(err.message || 'Failed to pin/unpin');
    }
  };

  const toggleLock = async (threadId, current) => {
    try {
      await updateDoc(doc(db, 'threads', threadId), { locked: !current });
    } catch (err) {
      console.error('Lock toggle failed', err);
      setError(err.message || 'Failed to lock/unlock');
    }
  };

  const removeThread = async (threadId) => {
    try {
      if (!window.confirm('Delete this thread? This will remove all comments and votes.')) return;
      setDeletingId(threadId);
      const threadRef = doc(db, 'threads', threadId);
      const commentsSnap = await getDocs(collection(db, 'threads', threadId, 'comments'));
      await Promise.all(commentsSnap.docs.map((d) => deleteDoc(d.ref)));
      const votesSnap = await getDocs(collection(db, 'threads', threadId, 'votes'));
      await Promise.all(votesSnap.docs.map((d) => deleteDoc(d.ref)));
      await deleteDoc(threadRef);
    } catch (err) {
      console.error('Delete failed', err);
      setError(err.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const voteThread = async (threadId, requestedValue) => {
    try {
      if (!user) { navigate('/login'); return; }
      const threadRef = doc(db, 'threads', threadId);
      const voteRef = doc(db, 'threads', threadId, 'votes', user.uid);
      await runTransaction(db, async (tx) => {
        const [threadSnap, voteSnap] = await Promise.all([tx.get(threadRef), tx.get(voteRef)]);
        if (!threadSnap.exists()) throw new Error('Thread missing');
        const oldVal = voteSnap.exists() ? (voteSnap.data().value || 0) : 0;
        const newVal = (requestedValue === oldVal) ? 0 : requestedValue; // toggle if same
        const delta = newVal - oldVal;
        if (delta !== 0) {
          const currentScore = typeof threadSnap.data().score === 'number' ? threadSnap.data().score : 0;
          tx.update(threadRef, { score: currentScore + delta });
        }
        tx.set(voteRef, { value: newVal, updatedAt: serverTimestamp() }, { merge: true });
      });
    } catch (err) {
      console.error('Vote failed', err);
      setError(err.message || 'Failed to vote');
    }
  };

  return (
    <div className="page-section">
      <div className="page-container forum-container">
        <h1 className="title" style={{ textAlign: 'center', marginTop: 0 }}>Forums</h1>
        <p className="subtitle" style={{ textAlign: 'center' }}>Discuss projects, liveries, and ideas.</p>

        <div className="glass-card form-glass" style={{ padding: 16, marginBottom: 16 }}>
          {error && <div style={{ color: '#ff6b6b', marginBottom: 8 }}>{error}</div>}
          <form onSubmit={createThread}>
            <div className="form-row" style={{ marginBottom: 8 }}>
              <input
                type="text"
                placeholder="Start a new thread…"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input-glass"
              />
            </div>
            <div className="form-row">
              <button disabled={creating || !title.trim()} type="submit" className="btn-glass-primary btn-block">
                {creating ? 'Creating…' : 'Post'}
              </button>
            </div>
          </form>
        </div>

        <div className="glass-card forum-list" style={{ padding: 0 }}>
          {threads.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', opacity: 0.85 }}>No threads yet. Be the first to post!</div>
          ) : (
            <ul className="forum-thread-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {threads.map((t) => (
                <li key={t.id} className="forum-thread-item">
                  <Link to={`/forums/${t.id}`} className="forum-thread-link">
                    <div className="forum-thread-row">
                      <div className="forum-thread-left">
                        {(() => {
                          let localPreview = null;
                          try { if (user && user.uid === t.authorId) localPreview = localStorage.getItem(`LOCAL_AVATAR_${user.uid}`); } catch (_) {}
                          const avatarUrl = t.authorPhotoURL || ((user && user.uid === t.authorId) ? (user.photoURL || localPreview || null) : null);
                          return avatarUrl ? (
                            <img src={avatarUrl} alt="avatar" className="forum-avatar" />
                          ) : (
                            <div className="forum-avatar forum-avatar-fallback" />
                          );
                        })()}
                        <div>
                          <div className="forum-thread-title">{t.title}</div>
                          <div className="meta-text forum-thread-meta">
                            by {t.authorName || 'Unknown'}
                            {t.locked ? ' · locked' : ''}
                            {t.pinned ? ' · pinned' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="meta-text forum-thread-right">{t.replies || 0} replies</div>
                    </div>
                  </Link>
                  {isMod && (
                    <div className="forum-thread-actions">
                      <button className="nav-link" onClick={() => togglePin(t.id, t.pinned)}>
                        {t.pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button className="nav-link" onClick={() => toggleLock(t.id, t.locked)}>
                        {t.locked ? 'Unlock' : 'Lock'}
                      </button>
                    </div>
                  )}
                  {/* Voting controls visible to all signed-in users */}
                  <div className="forum-thread-footer">
                    <button className="btn-glass-secondary" onClick={() => voteThread(t.id, 1)}>▲ Upvote</button>
                    <div className="meta-text">Score: {typeof t.score === 'number' ? t.score : 0}</div>
                    <button className="btn-glass-secondary" onClick={() => voteThread(t.id, -1)}>▼ Downvote</button>
                    {isAdmin && (
                      <button className="btn-glass-danger" onClick={() => removeThread(t.id)} disabled={deletingId === t.id}>
                        {deletingId === t.id ? 'Deleting…' : 'Delete thread'}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
